import { FC, useMemo } from "react";
import { useSelector } from "react-redux";

import isNil from "lodash/isNil";

import Image from "next/legacy/image";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

import { PRICE_LABEL_SIZE } from "./types";

import { selectTokens } from "redux/slices/tokens";

import {
  BSC_TOKENS,
  PAYMENT_FIATS,
  PAYMENT_TOKENS,
  POLYGON_TOKENS,
} from "enums/paymentTokens";

import { formatPaymentToken } from "utils/formatter";
import { formatCryptoPrice } from "utils/priceFormatters";

const PriceLabelContent = ({ children, size, bold }) => {
  return (
    <>
      {size === PRICE_LABEL_SIZE.SMALL && (
        <Typography variant="body2">
          {bold ? <b>{children}</b> : children}
        </Typography>
      )}
      {size === PRICE_LABEL_SIZE.MEDIUM && (
        <Typography variant="h5">
          {bold ? <b>{children}</b> : children}
        </Typography>
      )}
      {size === PRICE_LABEL_SIZE.LARGE && (
        <Typography component="h3" variant="h3">
          {bold ? <b>{children}</b> : children}
        </Typography>
      )}
    </>
  );
};

type PriceLabelProps = {
  value?: number;
  discount?: number | null;
  currency?: PAYMENT_TOKENS | POLYGON_TOKENS | BSC_TOKENS | PAYMENT_FIATS;
  size?:
    | typeof PRICE_LABEL_SIZE.SMALL
    | typeof PRICE_LABEL_SIZE.MEDIUM
    | typeof PRICE_LABEL_SIZE.LARGE;
  bold?: boolean;
  revert?: boolean;
};

const PriceLabel: FC<PriceLabelProps> = ({
  value,
  currency,
  discount = null,
  size = PRICE_LABEL_SIZE.MEDIUM,
  bold = false,
  revert = false,
}) => {
  const tokens = useSelector(selectTokens);
  const currencyUpper = currency?.toUpperCase();
  const token = useMemo(() => {
    return tokens.find((token) => token.name === currency);
  }, [currency, tokens]);

  return (
    <Box>
      <Box display="flex" alignItems="center">
        <Box>
          <PriceLabelContent size={size} bold={bold}>
            <Typography
              component="span"
              sx={discount ? { textDecoration: "line-through" } : {}}
            >
              {discount && !isNil(value)
                ? formatCryptoPrice(((value ?? 0) * 100) / (discount * 100))
                : formatCryptoPrice(value)}
            </Typography>
            {discount ? `/${formatCryptoPrice(value)}` : null}
          </PriceLabelContent>
        </Box>

        <Box
          display="flex"
          flexDirection={revert ? "row-reverse" : "row"}
          alignItems="center"
        >
          <Box
            ml={0.5}
            mr={
              currencyUpper === PAYMENT_TOKENS.ETH ||
              currencyUpper === PAYMENT_TOKENS.WETH ||
              currencyUpper === PAYMENT_TOKENS.BANANA ||
              currencyUpper === POLYGON_TOKENS.WETH_POLYGON
                ? 0
                : 0.5
            }
          >
            <PriceLabelContent size={size} bold={bold}>
              {currency && formatPaymentToken(currency)}
            </PriceLabelContent>
          </Box>

          {currency && (
            <Image
              src={
                token?.icon
                  ? `${process.env.NEXT_PUBLIC_CLOUDFRONT_URL}/pages${token.icon}`
                  : `${
                      process.env.NEXT_PUBLIC_CLOUDFRONT_URL
                    }/pages/icons/${currency.toLowerCase()}.svg`
              }
              width={size === PRICE_LABEL_SIZE.LARGE ? "30" : "20"}
              height={size === PRICE_LABEL_SIZE.LARGE ? "30" : "20"}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PriceLabel;
